'use client';

/**
 * LoadingBoundary Component
 *
 * A declarative loading state wrapper that eliminates the repeated
 * `if (isLoading) return X; if (error) return Y;` pattern across portals.
 *
 * Render priority: loading > error > empty > children
 *
 * Usage:
 * ```tsx
 * // Basic usage with skeleton string shorthand
 * <LoadingBoundary isLoading={isLoading} error={error} skeleton="table">
 *   <DataTable data={data} />
 * </LoadingBoundary>
 *
 * // Overlay mode — shows children with spinner on top
 * <LoadingBoundary isLoading={isSaving} overlay>
 *   <Form />
 * </LoadingBoundary>
 *
 * // With empty state
 * <LoadingBoundary isLoading={isLoading} isEmpty={data.length === 0} emptyState={<NoResults />}>
 *   <DataTable data={data} />
 * </LoadingBoundary>
 * ```
 */

import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { ReactNode } from 'react';
import type { NeonAccent } from '../../theme/palette';
import { iconSizes } from '../../theme/typography';
import type { ContentLoaderLayout } from '../ContentLoader';
import { ContentLoader } from '../ContentLoader';
import { LoadingOverlay } from '../LoadingOverlay';

// ============================================================================
// Types
// ============================================================================

const LAYOUT_SHORTHANDS: ReadonlySet<string> = new Set<ContentLoaderLayout>([
  'table',
  'cards',
  'dashboard',
  'form',
  'detail',
  'list',
]);

export interface LoadingBoundaryProps {
  /** Whether data is loading */
  isLoading: boolean;
  /** Error to display (Error object, string, or null) */
  error?: Error | string | null;
  /** Whether the data set is empty (checked only when not loading and no error) */
  isEmpty?: boolean;
  /** Skeleton during loading: ReactNode or layout shorthand string */
  skeleton?: ReactNode | ContentLoaderLayout;
  /** Custom empty state element */
  emptyState?: ReactNode;
  /** If true, shows LoadingOverlay on top of children instead of replacing them */
  overlay?: boolean;
  /** Neon accent color for overlay spinner */
  neonAccent?: NeonAccent;
  /** Content to render when data is ready */
  children: ReactNode;
  /** Additional MUI sx overrides for the container */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Helpers
// ============================================================================

function isLayoutShorthand(value: unknown): value is ContentLoaderLayout {
  return typeof value === 'string' && LAYOUT_SHORTHANDS.has(value);
}

function getErrorMessage(error: Error | string): string {
  return error instanceof Error ? error.message : error;
}

// ============================================================================
// Component
// ============================================================================

export const LoadingBoundary: React.FC<LoadingBoundaryProps> = ({
  isLoading,
  error,
  isEmpty,
  skeleton,
  emptyState,
  overlay,
  neonAccent,
  children,
  sx,
}) => {
  // ── Priority 1: Loading (overlay mode) ──────────────────────────────
  if (isLoading && overlay) {
    return (
      <Box sx={[{ position: 'relative' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
        {children}
        <LoadingOverlay visible={true} neonAccent={neonAccent} />
      </Box>
    );
  }

  // ── Priority 1: Loading (replace mode) ──────────────────────────────
  if (isLoading) {
    // Layout shorthand string → ContentLoader
    if (isLayoutShorthand(skeleton)) {
      return (
        <Box sx={sx}>
          <ContentLoader layout={skeleton} />
        </Box>
      );
    }

    // Custom ReactNode skeleton
    if (skeleton != null) {
      return <Box sx={sx}>{skeleton}</Box>;
    }

    // Default: LoadingOverlay standalone (not overlaying children)
    return (
      <Box sx={[{ position: 'relative', minHeight: 120 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
        <LoadingOverlay visible={true} neonAccent={neonAccent} />
      </Box>
    );
  }

  // ── Priority 2: Error ───────────────────────────────────────────────
  if (error != null && error !== '') {
    const message = getErrorMessage(error as Error | string);
    return (
      <Box
        role="alert"
        sx={[
          {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            py: 6,
            px: 3,
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <ErrorOutlinedIcon sx={{ fontSize: iconSizes['2xl'], color: 'error.main' }} />
        <Typography variant="body1" sx={{ color: 'error.main', textAlign: 'center' }}>
          {message}
        </Typography>
      </Box>
    );
  }

  // ── Priority 3: Empty ───────────────────────────────────────────────
  if (isEmpty && emptyState != null) {
    return <Box sx={sx}>{emptyState}</Box>;
  }

  // ── Default: Children ───────────────────────────────────────────────
  return <>{children}</>;
};

LoadingBoundary.displayName = 'LoadingBoundary';

export default LoadingBoundary;
